/**
 * /agents observability dashboard.
 *
 * Read-only V1: shows what each of the 8 background agents did most recently,
 * cross-cutting tables for open issues / cycle-log activity / findings docs,
 * and an inline render of docs/loop/state.md.
 *
 * Auth gate: AGENTS_ALLOWED_EMAILS env on the backend → /api/agents/access.
 */
import { useEffect } from 'react';
import { Box, Container, Typography, Button, Alert, Skeleton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useAgentsAccess } from '../hooks/useAgentsAccess';
import { useAgentsStore } from '../stores/agentsStore';
import { EcosystemKPIs } from '../components/Agents/EcosystemKPIs';
import { AgentCard } from '../components/Agents/AgentCard';
import { LoopConfigPanel } from '../components/Agents/LoopConfigPanel';
import { OpenIssuesTable } from '../components/Agents/OpenIssuesTable';
import { RecentCycleLogFeed } from '../components/Agents/RecentCycleLogFeed';
import { RecentFindingsTable } from '../components/Agents/RecentFindingsTable';
import { StateMdViewer } from '../components/Agents/StateMdViewer';

export function AgentsPage() {
  const navigate = useNavigate();
  const { hasAccess, loading: accessLoading } = useAgentsAccess();
  const {
    overview,
    agents,
    issues,
    prs,
    emails,
    stateMd,
    config,
    loading,
    error,
    lastRefreshedAt,
    loadAll,
    refresh,
  } = useAgentsStore();

  useEffect(() => {
    if (hasAccess && agents.length === 0 && !loading) {
      void loadAll();
    }
  }, [hasAccess, agents.length, loading, loadAll]);

  if (accessLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }} data-testid="agents-access-denied">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Alert severity="warning">
          You do not have access to the /agents observability page. If you believe this is wrong,
          ask an admin to add your email to <code>AGENTS_ALLOWED_EMAILS</code>.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="agents-page">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1">
            Agents
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lastRefreshedAt
              ? `Last refresh: ${new Date(lastRefreshedAt).toLocaleString()}`
              : 'Not yet loaded'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void refresh()}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <EcosystemKPIs overview={overview} loading={loading} />

      <LoopConfigPanel config={config} />

      <Box sx={{ display: 'grid', gap: 2, mb: 4 }}>
        {agents.map((agent) => (
          <AgentCard key={agent.name} agent={agent} prs={prs} emails={emails} />
        ))}
      </Box>

      <OpenIssuesTable issues={issues} />

      <RecentCycleLogFeed agents={agents} />

      <RecentFindingsTable agents={agents} />

      <StateMdViewer stateMd={stateMd} />
    </Container>
  );
}
